import * as constants from "@/config/constants";
import {
	MEDIA_ALPHA_BENEFITS_MAP,
	MEDIA_ALPHA_MINIMUM_BIDS,
	USE_MEDIA_ALPHA_COMPANY_LOGO,
	USE_MEDIA_ALPHA_HEADLINE,
} from "@/config/media-alpha";
import * as fvyConstants from "@/config/site/favy";
import * as irConstants from "@/config/site/insuranceranked";
import * as rcConstants from "@/config/site/ratecompare";
import * as tcrConstants from "@/config/site/thecreditreview";
import { resolveMainBenefitsField, resolveRatingKey } from "@/helpers";
import { path, pathOr, pick, prop, propOr, toPairs } from "ramda";
import createCachedSelector from "re-reselect";
import { createSelector } from "reselect";

import { DEFAULT_REVIEWS_PATH } from "../../config/constants";
import { FIXED_WIDTH_CARD_VERTICALS } from "../../config/grid";
import {
	formatMediaAlphaBenefits,
	formatServiceRightColumns,
	removeCharacterEntities,
	returnImageURL,
} from "../helpers/formatters";
import { logError } from "../helpers/logError";
import { replaceYearWithCurrent } from "../helpers/replaceYearWithCurrent";
import { generateCompanyVisitPath } from "../links/calculateLinkPaths";
import { categoryInfoSelector, getCategoryName } from "./categoryInfo";
import { getIsMediaAlphaTargetedPath } from "./mediaAlpha";

export const getStateForSentryExceptions = (state) => ({
	state: JSON.stringify(
		pick(
			[
				constants.MEDIA_ALPHA_FEED,
				constants.GEO_TARGETING,
				constants.MULTISTEP_MODAL,
			],
			state,
		),
		null,
		/* eslint-disable-next-line */
		2,
	),
});

/* Filtering selectors */
export const getIsTransitionApplied = (state) =>
	path([constants.FILTERING, constants.IS_TRANSITION_APPLIED], state);
export const getIsNotUserFiltered = (state) =>
	path([constants.FILTERING, [constants.IS_NOT_USER_FILTERED]], state);
export const getFilter = (state) => {
	const loadWithFilteringParameters = path(
		[constants.FILTERING, constants.LOAD_WITH_FILTERING_PARAMETERS],
		state,
	);
	const filter = path([constants.ROUTING_PARAMS, "filter"], state);

	return loadWithFilteringParameters ?? filter;
};
export const getAllFiltering = (state) => prop(constants.FILTERING, state);
export const getFilteringByKey = (state, props) =>
	path([constants.FILTERING, props.filteringKey], state);
export const getAged = (state) =>
	path([constants.FILTERING, irConstants.AGED_FILTERING_KEY], state);
export const getPolicyType = (state) =>
	path([constants.FILTERING, irConstants.POLICY_TYPE_FILTERING_KEY], state);
export const getLoanAmount = (state) =>
	path([constants.FILTERING, tcrConstants.LOAN_AMOUNT_FILTERING_KEY], state);
export const getCreditQuality = (state) =>
	path([constants.FILTERING, tcrConstants.CREDIT_QUALITY_FILTERING_KEY], state);
export const getFundingType = (state) =>
	path([constants.FILTERING, tcrConstants.FUNDING_TYPE_FILTERING_KEY], state);
export const getMonthlyRevenue = (state) =>
	path(
		[constants.FILTERING, tcrConstants.MONTHLY_REVENUE_FILTERING_KEY],
		state,
	);
export const getGender = (state) =>
	path([constants.FILTERING, fvyConstants.HAIR_GROWTH_FILTERING_KEY], state);
export const getMortgageType = (state) =>
	path([constants.FILTERING, rcConstants.MORTGAGE_TYPE_FILTERING_KEY], state);
export const getLoanAmountServiced = (state) =>
	path(
		[constants.FILTERING, rcConstants.LOAN_AMOUNT_SERVICED_FILTERING_KEY],
		state,
	);

export const getVariationId = (state) =>
	path([constants.FILTERING, constants.VARIATION_ID], state);

/* Geotargeting selectors */
export const getUserGeo = (state) =>
	path([constants.GEO_TARGETING, "region"], state);
export const getUserZip = (state) =>
	path([constants.GEO_TARGETING, "zip"], state);

/* Vertical Information selectors*/
export const getAssets = (state) =>
	path([constants.VERTICAL_INFORMATION, "assets"], state);

/* Routing params / location selectors*/
export const getAllRoutingParams = (state) =>
	path([constants.ROUTING_PARAMS], state);
export const getIsStandardTargetedPath = (state) =>
	pathOr(false, [constants.ROUTING_PARAMS, "isTargetedPath"], state);
export const getIsTargetedPath = createSelector(
	[getIsStandardTargetedPath, getIsMediaAlphaTargetedPath],
	(isStandardTargetedPath, isMediaAlphaTargetedPath) =>
		isStandardTargetedPath || isMediaAlphaTargetedPath,
);
export const getVertical = (state) =>
	path([constants.ROUTING_PARAMS, "vertical"], state);
export const getCompanyPositionOverride = (state) =>
	path([constants.COMPANY_RANKING, "targetedTopCompanyOverride"], state);

export const getVerticalConfig = createSelector([getVertical], (vertical) =>
	pathOr(
		{
			preventRenderingCompanyListHeader: true,
			showFeaturedCompany: true,
		},
		[vertical],
		constants.SITE_INFORMATION,
	),
);

export const getIsMobileTargetedPath = (state) =>
	path([constants.ROUTING_PARAMS, "isMobileTargetedPath"], state);

export const getTargetedSlug = (state) =>
	path([constants.ROUTING_PARAMS, "targetedSlug"], state);

/* company selectors */
export const getAllCompanySpecificQueryParams = (state) =>
	prop(constants.COMPANY_QUERY_PARAMS, state);
export const getContentfulCompanies = (state) =>
	path([constants.VERTICAL_INFORMATION, "companies"], state);
export const getFeaturedCompany = (state) =>
	path([constants.FEATURED_COMPANY, "company"], state);

export const getMediaAlphaMinimumBid = createSelector(getVertical, (vertical) =>
	prop(vertical, MEDIA_ALPHA_MINIMUM_BIDS),
);

/* Selectors for MediaAlpha */
export const getIsMediaAlphaPublisherVertical = createSelector(
	getVerticalConfig,
	(verticalConfig) =>
		pathOr(false, ["isMediaAlphaPublisherVertical"], verticalConfig),
);

export const getSortCompaniesByMediaAlphaBid = createSelector(
	getVerticalConfig,
	(verticalConfig) =>
		pathOr(false, ["companiesSortedByMediaAlphaBids"], verticalConfig),
);

export const getMultiStepModalConfig = createSelector(
	getVerticalConfig,
	(verticalConfig) => pathOr(false, ["mediaAlphaModalSteps"], verticalConfig),
);

export const getUsesMultiStepModal = createSelector(
	[getVerticalConfig, getMultiStepModalConfig],
	(verticalConfig, multiStepModalConfig) =>
		multiStepModalConfig
			? pathOr(false, ["usesMultiStepModal"], verticalConfig)
			: false,
);

export const getUsesMediaAlphaHeadline = createSelector(
	getVertical,
	(vertical) => propOr(false, vertical, USE_MEDIA_ALPHA_HEADLINE),
);

export const getUrlPathFilteringConfiguration = createSelector(
	getVerticalConfig,
	(verticalConfig) =>
		pathOr(
			{},
			["filteringInformation", "urlPathFilteringConfiguration"],
			verticalConfig,
		),
);

export const getDefaultFilteringState = createSelector(
	getVerticalConfig,
	(verticalConfig) =>
		pathOr(
			[],
			["filteringInformation", "defaultFilteringState"],
			verticalConfig,
		),
);

export const getInitialFilteringState = createSelector(
	getFilter,
	getUrlPathFilteringConfiguration,
	getDefaultFilteringState,
	(filter, urlPathFilteringConfig, defaultFilteringState) => {
		// URL-based filter takes precedence
		if (filter && urlPathFilteringConfig.hasOwnProperty(filter)) {
			return urlPathFilteringConfig[filter] || [];
		}

		return defaultFilteringState;
	},
);

export const getVerticalFilterFunctions = createSelector(
	getVerticalConfig,
	(verticalConfig) =>
		pathOr({}, ["filteringInformation", "filterFunctions"], verticalConfig),
);

export const getFilteringControls = createSelector(
	getVerticalConfig,
	(verticalConfig) =>
		path(["filteringInformation", "filteringControls"], verticalConfig),
);

export const getModalFilteringControls = createSelector(
	getVerticalConfig,
	(verticalConfig) =>
		path(["filteringInformation", "modalFilteringControls"], verticalConfig),
);

export const companySelector = (_, props) => prop("company", props);

const getCompanyCacheKey = (_, props) =>
	-path(["company", "fields", "companyName"], props);

export const getCompanySlug = createSelector(companySelector, (company) =>
	path(["fields", "companySlug"], company),
);

export const getCompanyName = createSelector(companySelector, (company) =>
	path(["fields", "companyName"], company),
);

export const getIsFeaturedCompany = createSelector(companySelector, (company) =>
	pathOr(false, ["fields", "isFeatured"], company),
);

export const getCompanySpecificQueryParams = createSelector(
	[getAllCompanySpecificQueryParams, getCompanyName, getCompanySlug],
	(allCompanySpecificQueryParams, companyName, companySlug) => {
		const companySpecificQueryParams = allCompanySpecificQueryParams.find(
			(obj) => prop(companyName, obj) || prop(companySlug, obj),
		);

		let queryParams = [];
		if (companySpecificQueryParams) {
			if (companySpecificQueryParams[companyName]) {
				queryParams = toPairs(companySpecificQueryParams[companyName]);
			} else if (companySpecificQueryParams[companySlug]) {
				queryParams = toPairs(companySpecificQueryParams[companySlug]);
			}
		}
		return queryParams;
	},
);

export const getFilteringParameters = createSelector(
	getAllFiltering,
	(filteringInformation) => {
		if (!filteringInformation) return {};

		// Use object destructuring to exclude restricted params
		const {
			[constants.IS_TRANSITION_APPLIED]: _transition,
			[constants.IS_NOT_USER_FILTERED]: _userFiltered,
			...filteringParams
		} = filteringInformation;

		// Filter out undefined values
		return Object.fromEntries(
			Object.entries(filteringParams).filter(
				([_, value]) => value !== undefined,
			),
		);
	},
);

export const getFilteringParametersCount = createSelector(
	[getFilteringParameters, getVertical],
	(filteringParameters, vertical) => {
		let count = Object.keys(filteringParameters).length;

		// TODO: FUID-1627: Gracefully handle filters which shouldn't be visible to the user
		if (constants.SITE === constants.TCR_DOMAIN) {
			if (vertical === tcrConstants.PERSONAL_LOANS_DEFAULT_PATH && count > 1) {
				count -= 1;
			}
		}

		return count;
	},
);

export const getVisitLinkQueryString = createSelector(
	[getFilteringParameters, getCompanySpecificQueryParams, getIsFeaturedCompany],
	(filteringParameters, companySpecificQueryParams, isFeaturedCompany) => {
		const queryStringArray = [
			...Object.entries(filteringParameters).map(([key, value]) => [
				key,
				value.toString().toLowerCase().replace(/\s/g, "-"),
			]),
			...companySpecificQueryParams,
		];
		if (isFeaturedCompany) queryStringArray.push(["ffp", "1"]);
		const queryString = queryStringArray
			.map((property) => property.join("="))
			.join("&");
		return queryString ? `&${queryString}` : queryString;
	},
);

export const getIsAmazonVertical = (state) =>
	path([constants.VERTICAL_INFORMATION, "isAmazonVertical"], state);

export const getIsFixedWidthVertical = createSelector(
	getVertical,
	getIsAmazonVertical,
	(vertical, isAmazonVertical) =>
		FIXED_WIDTH_CARD_VERTICALS.includes(vertical) || isAmazonVertical,
);

export const getLinkCallToActionText = createSelector(
	[categoryInfoSelector],
	(categoryInfo) => path(["0", "fields", "linkCallToActionText"], categoryInfo),
);

export const getReviewLinkWithoutDateText = createSelector(
	categoryInfoSelector,
	(categoryInfo) =>
		path(["0", "fields", "reviewLinkWithoutDateText"], categoryInfo),
);

export const getServiceBlockCallOutInformation = createSelector(
	getVerticalConfig,
	(verticalConfig) => path(["serviceBlockCallout"], verticalConfig),
);

export const getCompanyRatingKey = createSelector(
	[getVertical, getFilter, getAllFiltering, getIsMobileTargetedPath],
	(vertical, filter, filteringInformation, isMobileTargetedPath) =>
		resolveRatingKey(
			vertical,
			filter,
			filteringInformation,
			isMobileTargetedPath,
		),
);

export const getIsDefaultColumnConfig = createSelector(
	[getVerticalConfig],
	(verticalConfig) => !path(["serviceRightColumns"], verticalConfig),
);

export const getHeaderParts = createSelector(
	[getVerticalConfig, getIsDefaultColumnConfig],
	(verticalConfig, isDefaultColumnConfig) =>
		isDefaultColumnConfig
			? constants.DEFAULT_COMPANY_LIST_HEADER_PARTS
			: pathOr(
					constants.DEFAULT_COMPANY_LIST_HEADER_PARTS,
					["companyListHeaderParts"],
					verticalConfig,
				),
);

export const getMediaAlphaBanner = createSelector(
	getVerticalConfig,
	(verticalConfig) => pathOr(false, ["usesMediaAlphaBanner"], verticalConfig),
);

/* Selectors pulling from individual Company fields */
const getCompanyIndex = (_, props) => prop("companyIndex", props);

export const getPromotionText = createCachedSelector(
	[companySelector],
	(company) => path(["fields", "promotionText"], company),
)(getCompanyCacheKey);

export const getIsMediaAlphaCompany = createCachedSelector(
	[companySelector],
	(company) => pathOr(false, ["fields", "isMediaAlpha"], company),
)(getCompanyCacheKey);

export const getIsDirectCompanyFromPayload = createCachedSelector(
	[companySelector],
	(company) => pathOr(false, ["fields", "is_direct"], company),
)(getCompanyCacheKey);

export const getMediaAlphaHeadline = createCachedSelector(
	[
		companySelector,
		getIsMediaAlphaPublisherVertical,
		getUsesMediaAlphaHeadline,
		getIsMediaAlphaCompany,
	],
	(
		company,
		isMediaAlphaPublisherVertical,
		usesMediaAlphaHeadline,
		isMediaAlphaCompany,
	) => {
		const isHeadlineAllowed =
			isMediaAlphaPublisherVertical && usesMediaAlphaHeadline;
		if (isHeadlineAllowed) {
			if (isMediaAlphaCompany) {
				const mediaAlphaHeadline = isHeadlineAllowed
					? pathOr(false, ["fields", "headline"], company)
					: false;
				return mediaAlphaHeadline
					? removeCharacterEntities(mediaAlphaHeadline)
					: mediaAlphaHeadline;
			} else {
				return pathOr(false, ["fields", "companyHeadline"], company);
			}
		} else return false;
	},
)(getCompanyCacheKey);

export const getUsesMediaAlphaBenefits = createSelector(
	[getIsMediaAlphaPublisherVertical, getIsMediaAlphaCompany, getVertical],
	(isMediaAlphaPublisherVertical, isMediaAlphaCompany, vertical) =>
		isMediaAlphaPublisherVertical &&
		propOr(false, vertical, MEDIA_ALPHA_BENEFITS_MAP) &&
		isMediaAlphaCompany,
);

export const getCompanySlugForVisitPath = createSelector(
	[getCompanySlug, getIsMediaAlphaTargetedPath],
	(companySlug, isMediaAlphaTargetedPath) => {
		if (isMediaAlphaTargetedPath) {
			return `${companySlug}-ma`;
		}

		return companySlug;
	},
);

export const getCouponCode = createCachedSelector(
	[companySelector],
	(company) => path(["fields", "couponCode"], company),
)(getCompanyCacheKey);

export const getCouponDescription = createCachedSelector(
	[companySelector],
	(company) => path(["fields", "couponDescription"], company),
)(getCompanyCacheKey);

export const getCompanyRating = createCachedSelector(
	[getCompanyRatingKey, companySelector],
	(ratingKey, company) => path(["fields", ratingKey], company),
)(getCompanyCacheKey);

export const getAwardText = createCachedSelector([companySelector], (company) =>
	path(["fields", "awardText"], company, null),
)(getCompanyCacheKey);

export const getAmBestRating = createCachedSelector(
	[companySelector],
	(company) => path(["fields", "amBestRating"], company, null),
)(getCompanyCacheKey);

export const getPrice = createCachedSelector([companySelector], (company) =>
	path(["fields", "price"], company),
)(getCompanyCacheKey);

export const getMainBenefits = createCachedSelector(
	[
		getVertical,
		getFilter,
		getIsMobileTargetedPath,
		getAllFiltering,
		companySelector,
		getUsesMediaAlphaBenefits,
	],
	(
		vertical,
		filter,
		isMobileTargetedPath,
		filteringInformation,
		company,
		usesMediaAlphaBenefits,
	) => {
		const nonMediaAlphaBenefitsField = resolveMainBenefitsField(
			vertical,
			filter,
			isMobileTargetedPath,
			filteringInformation,
			company,
		);
		const mediaAlphaBenefits = path(["fields", "description"], company);
		if (usesMediaAlphaBenefits) {
			return formatMediaAlphaBenefits(mediaAlphaBenefits);
		}

		return removeCharacterEntities(
			pathOr(
				path(["fields", "companyMainBenefits"], company),
				["fields", nonMediaAlphaBenefitsField],
				company,
			),
		);
	},
)(getCompanyCacheKey);

const getDefaultColumnConfig = createSelector(
	getVerticalConfig,
	(verticalConfig) =>
		pathOr(
			constants.DEFAULT_SERVICE_BLOCK_COLUMNS,
			["serviceRightColumns"],
			verticalConfig,
		),
);

export const getCompanyListHeaderColumnCount = createSelector(
	getDefaultColumnConfig,
	(defaultColumnConfig) => defaultColumnConfig.length,
);

export const getServiceBlockColumnConfig = createSelector(
	[getDefaultColumnConfig],
	(defaultColumnConfig) => defaultColumnConfig,
);

export const getColumnInformation = createCachedSelector(
	[getServiceBlockColumnConfig, companySelector],
	(columnConfig, company) =>
		columnConfig
			? formatServiceRightColumns(company, columnConfig)
			: columnConfig,
)(getCompanyCacheKey);

export const getServiceBlockColumnCount = createSelector(
	getServiceBlockColumnConfig,
	(columnConfig) => columnConfig.length,
);

export const getShowFeaturedCompany = createSelector(
	getVerticalConfig,
	(verticalConfig) => pathOr(false, ["showFeaturedCompany"], verticalConfig),
);

export const getDeemphasizedCompanySlugs = createSelector(
	getVerticalConfig,
	(verticalConfig) => pathOr([], ["deemphasizedCompanySlugs"], verticalConfig),
);

export const getPreventRenderingCompanyListHeader = createSelector(
	[getVerticalConfig],
	(verticalConfig) =>
		pathOr(false, ["preventRenderingCompanyListHeader"], verticalConfig),
);

export const getUsesMediaAlphaCompanyLogo = createSelector(
	[getIsMediaAlphaPublisherVertical, getIsMediaAlphaCompany, getVertical],
	(isMediaAlphaPublisherVertical, isMediaAlphaCompany, vertical) =>
		isMediaAlphaPublisherVertical && isMediaAlphaCompany
			? propOr(false, vertical, USE_MEDIA_ALPHA_COMPANY_LOGO)
			: false,
);

export const getCompanyLogoUrl = createCachedSelector(
	[
		getAssets,
		companySelector,
		getUsesMediaAlphaCompanyLogo,
		getVertical,
		getStateForSentryExceptions,
	],
	(
		assets,
		company,
		usesMediaAlphaCompanyLogo,
		vertical,
		sentryExceptionState,
	) => {
		/* use large image URL, fallback to medium if large isn't available. Observed no large_image_url for farmers on auto insurance */
		const mediaAlphaLogoUrl = pathOr(
			path(["fields", "medium_image_url"], company),
			["fields", "large_image_url"],
			company,
		);
		const assetId = path(["fields", "companyLogo", "sys", "id"], company);
		const contentfulLogoUrl = returnImageURL(assets, assetId);
		if (
			usesMediaAlphaCompanyLogo &&
			!mediaAlphaLogoUrl &&
			!["lifeplans.com"].includes(company.fields.carrier)
		) {
			// use the array to prevent reporting Sentry company logo errors for a specific MA carrier
			logError(
				`No medium or large company image in MA payload for ${company.fields.carrier} on ${vertical}. Using company image from Contentful`,
			);
		}
		/* fallback logic to use Contentful image URL if for whatever reason the Media Alpha logo isn't available */
		if (usesMediaAlphaCompanyLogo && mediaAlphaLogoUrl) {
			return mediaAlphaLogoUrl;
		}

		return contentfulLogoUrl;
	},
)(getCompanyCacheKey);

export const getCompanyPhoneNumber = createCachedSelector(
	[companySelector],
	(company) => path(["fields", "phoneNumber"], company),
)(getCompanyCacheKey);

export const getCompanyPhoneNumberHref = createCachedSelector(
	[getCompanyPhoneNumber],
	(phoneNumber) =>
		phoneNumber ? `tel:+1${phoneNumber.replace(/ /g, "")}` : phoneNumber,
)(getCompanyCacheKey);

export const getTaxCafNumber = createCachedSelector(
	[companySelector],
	(company) => path(["fields", "cafNumber"], company),
)(getCompanyCacheKey);

export const getTrustpilotScore = createCachedSelector(
	[companySelector],
	(company) => path(["fields", "trustpilotReviewScore"], company),
)(getCompanyCacheKey);

export const getWebReviewScore = createCachedSelector(
	[companySelector],
	(company) => path(["fields", "webReviewScore"], company),
)(getCompanyCacheKey);

export const getTrustpilotReviewCount = createCachedSelector(
	[companySelector],
	(company) => path(["fields", "trustpilotReviewCount"], company),
)(getCompanyCacheKey);

export const getWebReviewCount = createCachedSelector(
	[companySelector],
	(company) => path(["fields", "webReviewCount"], company),
)(getCompanyCacheKey);

/* Selector for link outs */

export const getIsCompanyReviewLive = createCachedSelector(
	[companySelector],
	(company) =>
		!!(
			path(["fields", "companyReviewArticle"], company) ||
			path(["fields", "companyReview"], company)
		),
)(getCompanyCacheKey);

export const getIsCompanyReviewLinkable = createCachedSelector(
	[companySelector, getIsCompanyReviewLive],
	(company, isCompanyReviewLive) => isCompanyReviewLive,
)(getCompanyCacheKey);

export const getAlternativeReviewSlug = createCachedSelector(
	[companySelector],
	(company) => path(["fields", "companyReviewSlug"], company),
)(getCompanyCacheKey);

export const getReviewSlug = createCachedSelector(
	[getCompanySlug, getAlternativeReviewSlug],
	(companySlug, alternativeReviewSlug) => alternativeReviewSlug || companySlug,
)(getCompanyCacheKey);

export const getCompanyReviewPath = createCachedSelector(
	[getVertical, getReviewSlug],
	(vertical, reviewSlug) =>
		`/${vertical}/${DEFAULT_REVIEWS_PATH}/${reviewSlug}`,
)(getCompanyCacheKey);

export const getIntegratedPublisherVisitPath = createSelector(
	[companySelector, getVariationId],
	(company, variationId) => {
		const baseUrl = path(["fields", "click_url"], company);
		const queryString = variationId
			? `?${constants.VARIATION_ID}=${variationId}`
			: "";
		return `${baseUrl}${queryString}`;
	},
);

export const getUsesMediaAlphaClickUrl = createCachedSelector(
	companySelector,
	(company) => pathOr(false, ["fields", "useMediaAlphaClickUrl"], company),
)(getCompanyCacheKey);

export const getCompanyVisitPath = createCachedSelector(
	[
		getIsMediaAlphaCompany,
		getIsTargetedPath,
		getCompanySlugForVisitPath,
		getFilter,
		getVisitLinkQueryString,
		getCompanyIndex,
		getUsesMediaAlphaClickUrl,
		getIntegratedPublisherVisitPath,
	],
	(
		isMediaAlphaCompany,
		isTargetedPath,
		companySlugForVisitPath,
		filter,
		visitLinkQueryString,
		companyIndex,
		usesMediaAlphaClickUrl,
		integratedPublisherVisitPath,
	) => {
		if (isMediaAlphaCompany && usesMediaAlphaClickUrl) {
			return integratedPublisherVisitPath;
		} else {
			return generateCompanyVisitPath({
				isTargeted: isTargetedPath,
				visitLinkQueryString,
				filter,
				companySlug: companySlugForVisitPath,
				rank: companyIndex + 1,
			});
		}
	},
)(getCompanyCacheKey);

/* Targeted Page Entry selectors */
export const getTargetedPageEntry = (state) =>
	path([constants.TARGETED_PAGE_ENTRY], state);

export const getPageHeadline = createSelector(
	getTargetedPageEntry,
	getCategoryName,
	(targetedPageEntry, categoryName) => {
		if (targetedPageEntry?.pageHeadline) {
			return replaceYearWithCurrent(targetedPageEntry.pageHeadline);
		} else if (categoryName) {
			const currentYear = new Date().getFullYear();
			const cleanCategoryName = categoryName.replace(/services/i, "").trim();

			return `Best 5 ${cleanCategoryName} Companies in ${currentYear}`;
		}

		logError("Unable to retrieve page headline");
		return "";
	},
);
